import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useSwipeable } from "react-swipeable";

const transition = { type: "spring", stiffness: 280, damping: 28 };

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.85 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { ...transition, duration: 0.28 },
  },
  exit: {
    opacity: 0,
    scale: 0.9,
    transition: { ...transition, duration: 0.2 },
  },
};

const imageVariants = {
  enter: { opacity: 0, scale: 0.95 },
  center: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 1.05 },
};

const normalizeItems = (items = []) =>
  items.map((item, idx) =>
    typeof item === "string"
      ? { src: item, alt: `Gallery image ${idx + 1}`, caption: "" }
      : {
          src: item.src,
          alt: item.alt || `Gallery image ${idx + 1}`,
          caption: item.caption || "",
        }
  );

const useGalleryNavigation = (total, initial = 0) => {
  const [index, setIndex] = useState(initial);
  const next = () => setIndex((prev) => (prev + 1) % total);
  const prev = () => setIndex((prev) => (prev - 1 + total) % total);

  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") next();
    if (e.key === "ArrowLeft") prev();
    if (e.key === "Escape") e.target?.blur?.();
  };

  useEffect(() => {
    setIndex(initial);
  }, [initial, total]);

  return { index, setIndex, next, prev, onKeyDown };
};

const ModernGallery = ({ items = [], onIndexChange }) => {
  const normalized = useMemo(() => normalizeItems(items), [items]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(0);
  const { index, setIndex, next, prev, onKeyDown } = useGalleryNavigation(
    normalized.length,
    selected
  );

  useEffect(() => {
    if (open) {
      const handler = (e) => onKeyDown(e);
      window.addEventListener("keydown", handler);
      return () => window.removeEventListener("keydown", handler);
    }
  }, [open, onKeyDown]);

  useEffect(() => {
    setSelected(index);
    if (typeof onIndexChange === "function") {
      onIndexChange(index);
    }
  }, [index, onIndexChange]);

  if (!normalized.length) return null;

  const swipeHandlers = useSwipeable({
    onSwipedLeft: next,
    onSwipedRight: prev,
    trackMouse: true,
  });

  return (
    <div className="space-y-0">
      <div className="columns-1 sm:columns-2 lg:columns-3 gap-0">
        {normalized.map((item, idx) => (
          <div
            key={item.src + idx}
            className="mb-0 overflow-hidden rounded-2xl bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl"
            style={{ breakInside: "avoid" }}
          >
            <button
              type="button"
              onClick={() => {
                setSelected(idx);
                setIndex(idx);
                setOpen(true);
              }}
              className="group relative block w-full overflow-hidden"
            >
              <img
                src={item.src}
                alt={item.alt}
                loading="lazy"
                decoding="async"
                className="w-full h-auto transform-gpu transition duration-300 ease-out group-hover:scale-105"
              />
              {item.caption && (
                <div className="absolute inset-x-0 bottom-0 translate-y-4 opacity-0 bg-black/60 text-white px-4 py-3 text-sm backdrop-blur-sm transition duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                  {item.caption}
                </div>
              )}
            </button>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] flex items-center justify-center"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            <button
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setOpen(false)}
            />
            <div className="relative z-10 flex w-full max-w-5xl flex-col items-center px-4 sm:px-8">
              <motion.div
                className="relative w-full overflow-hidden rounded-3xl bg-stone-950/60 p-4 shadow-2xl ring-1 ring-white/10 backdrop-blur-xl"
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                transition={transition}
                {...swipeHandlers}
              >
                <div className="relative flex items-center justify-center">
                  {normalized.length > 1 && (
                    <button
                      className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-2 text-white shadow-lg transition hover:bg-black/80"
                      onClick={prev}
                    >
                      <ChevronLeft className="h-6 w-6" />
                    </button>
                  )}
                  <div className="w-full">
                    <motion.img
                      key={normalized[index].src}
                      src={normalized[index].src}
                      alt={normalized[index].alt}
                      loading="lazy"
                      decoding="async"
                      className="mx-auto max-h-[90vh] w-full max-w-6xl rounded-2xl object-contain transform-gpu"
                      variants={imageVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ ...transition, duration: 0.25 }}
                    />
                  </div>
                  {normalized.length > 1 && (
                    <button
                      className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-2 text-white shadow-lg transition hover:bg-black/80"
                      onClick={next}
                    >
                      <ChevronRight className="h-6 w-6" />
                    </button>
                  )}
                </div>
                {normalized[index].caption && (
                  <p className="mt-4 text-center text-sm text-white/80">
                    {normalized[index].caption}
                  </p>
                )}
                {normalized.length > 1 && (
                  <div className="mt-4 flex items-center justify-center gap-2 text-xs text-white/70">
                    {index + 1} / {normalized.length}
                  </div>
                )}

                <button
                  type="button"
                  aria-label="Close"
                  className="absolute right-3 top-3 rounded-full bg-black/60 p-2 text-white shadow-lg transition hover:bg-black/80"
                  onClick={() => setOpen(false)}
                >
                  <X className="h-5 w-5" />
                </button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ModernGallery;
